import {
  Flex,
  Icon,
  SystemProps,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useEffect } from "react";
import { HiVideoCamera, HiVideoCameraSlash } from "react-icons/hi2";

import { useIsSmallScreen } from "../../../hooks/useIsSmallScreen";
import { parseDate } from "../../../utils/datetime";
import {
  CaptureStatusEnum,
  ScheduledInterviewListItemFragment,
  WhenEnum,
} from "../../graphql";

type ScheduledInterview = Pick<
  ScheduledInterviewListItemFragment,
  "scheduledEnd" | "interviewType"
> & {
  captureStatus?: CaptureStatusEnum;
  isInProgress?: boolean;
  when?: WhenEnum;
};

type RecordingOutcomeMessageProps = {
  scheduledInterview: ScheduledInterview;
} & SystemProps;

const RecordingOutcomeMessage: React.FC<RecordingOutcomeMessageProps> = ({
  scheduledInterview,
  ...styles
}) => {
  const isSmallScreen = useIsSmallScreen();
  const isCaptured =
    scheduledInterview.captureStatus === CaptureStatusEnum.Captured;
  const { isInProgress, when, scheduledEnd } = scheduledInterview;

  const {
    isOpen: showingMobileTooltip,
    onOpen: showMobileTooltip,
    onClose: hideMobileTooltip,
  } = useDisclosure();
  /**
   * For mobile, clear tooltip after scroll or 2.5 seconds
   */
  useEffect(() => {
    if (!showingMobileTooltip) return;
    window.addEventListener("scroll", hideMobileTooltip, { once: true });
    const timeout = setTimeout(hideMobileTooltip, 2500);
    return () => {
      window.removeEventListener("scroll", hideMobileTooltip);
      clearTimeout(timeout);
    };
  }, [showingMobileTooltip]);

  // Don't render anything for future interviews
  if (when === WhenEnum.Future) {
    return null;
  }

  // For today's interviews, only show if in progress or 30 min after scheduled end
  if (when === WhenEnum.Today) {
    const thirtyMinutesAfterEnd = parseDate(scheduledEnd);
    thirtyMinutesAfterEnd.setUTCMinutes(
      thirtyMinutesAfterEnd.getUTCMinutes() + 30
    );
    const now = new Date();
    if (!isInProgress && now < thirtyMinutesAfterEnd) {
      return null;
    }
  }

  return (
    <Flex
      alignItems="center"
      color="gray.500"
      onClick={showMobileTooltip}
      mb={scheduledInterview.interviewType === "PHONE" ? "auto" : undefined}
      {...styles}
    >
      <>
        <Icon
          as={isInProgress || isCaptured ? HiVideoCamera : HiVideoCameraSlash}
          color={
            isInProgress ? "red.500" : isCaptured ? "green.500" : "gray.500"
          }
          boxSize="5"
        />
        <Tooltip
          label={
            isInProgress
              ? "The interview is in progress"
              : isCaptured
              ? "The interview was captured in BrightHire"
              : "The interview was not captured in BrightHire"
          }
          isOpen={isSmallScreen ? showingMobileTooltip : undefined}
          maxW="240"
          placement={isSmallScreen ? "auto-end" : undefined}
        >
          <Text
            fontSize="sm"
            fontWeight="500"
            ml="2"
            lineHeight="5"
            data-testid="scheduled-interview-recording-status-text"
            color={isInProgress ? "red.500" : undefined}
          >
            {isInProgress
              ? "In progress"
              : isCaptured
              ? "Recorded"
              : "Not Recorded"}
          </Text>
        </Tooltip>
      </>
    </Flex>
  );
};

export default RecordingOutcomeMessage;
